/**
 * Ce que l'écran dit après avoir affecté quelqu'un sur toutes les journées suivantes.
 *
 * *Chris, 15/09/2026 : « quand j'affecte sur la première journée une personne, tu peux l'affecter
 * sur toutes les journées suivantes, et appliquer aussi au matériel associé ».*
 *
 * ## Pourquoi une phrase, alors que le tableau se recharge de toute façon
 *
 * Parce que la propagation touche des jours **qu'on ne regarde pas** : on affecte le lundi, et
 * dix-neuf journées se remplissent en dessous, parfois hors de l'écran. Le rechargement les montre
 * à qui fait défiler ; la phrase les annonce à qui ne le fait pas.
 *
 * C'est la contrepartie de l'automatisme, et c'est elle qui distingue « le produit a fait ce que
 * j'attendais » de « il s'est passé quelque chose que je n'ai pas vu ».
 *
 * ## Ce qui est écarté compte plus que ce qui est posé
 *
 * Un jour posé se voit dans le tableau. Un jour **sauté** ne se voit pas : la case reste vide, et
 * une case vide au milieu de dix-neuf cases pleines se lit comme un oubli du produit. Les deux
 * raisons sont donc nommées — le besoin déjà couvert, et surtout **l'absence**, qui est la seule
 * qui demande une décision.
 *
 * ## Les absences non vérifiées se disent, toujours
 *
 * Quand le référentiel maître n'a pas répondu, on a posé sans savoir qui était là. Le taire ferait
 * lire « personne n'est absent » — exactement le mode de panne du 16/08/2026, où un écran muet
 * passait pour un écran rassurant.
 */

export type Serie = {
  readonly poses: readonly { readonly jourRelatif: number; readonly date: string | null }[];
  readonly ecartes: readonly {
    readonly jourRelatif: number;
    readonly date: string | null;
    readonly raison: string;
  }[];
  readonly materiel: {
    readonly poses: readonly { readonly libelle: string; readonly ligne: string }[];
    readonly ecartes: readonly { readonly libelle: string; readonly raison: string }[];
  };
  readonly absencesNonVerifiees: boolean;
} | null;

/** Une date civile « 2026-09-15 » se lit « 15/09 » — l'année est celle qu'on a sous les yeux. */
function jourCourt(date: string | null, jourRelatif: number): string {
  if (date === null) return `J${String(jourRelatif)}`;
  const [, mois, jour] = date.split("-");
  return `${jour ?? ""}/${mois ?? ""}`;
}

/**
 * La phrase, ou `null` quand il n'y a rien à dire.
 *
 * `null` est le cas d'une ligne d'un seul jour — fréquent — et celui d'une journée déjà remplie
 * partout : dans les deux cas, annoncer « rien n'a changé » serait du bruit.
 */
export function direLaSerie(serie: Serie): string | null {
  if (serie === null) return null;
  const morceaux: string[] = [];

  if (serie.poses.length > 0) {
    const jours = serie.poses.map((p) => jourCourt(p.date, p.jourRelatif));
    /*
      Au-delà de six jours, on donne le COMPTE et les bornes plutôt que la liste.

      Vingt dates alignées ne se lisent pas : ce qu'on veut savoir est « combien, et jusqu'où ».
      En dessous, la liste reste plus parlante que « 3 journées » — on reconnaît ses jours.
    */
    morceaux.push(
      jours.length > 6
        ? `Également affecté sur ${String(jours.length)} journées suivantes, jusqu'au ${jours[jours.length - 1] ?? ""}.`
        : `Également affecté le ${jours.join(", ")}.`,
    );
  }

  if (serie.materiel.poses.length > 0) {
    const machines = [...new Set(serie.materiel.poses.map((p) => p.libelle))];
    morceaux.push(`Matériel associé posé avec : ${machines.join(", ")}.`);
  }

  if (serie.ecartes.length > 0) {
    /*
      Les jours écartés sont groupés PAR RAISON.

      « 12/09, 13/09 : absent » se lit d'un coup ; la même information répétée jour par jour force
      à comparer trois phrases pour comprendre qu'elles disent la même chose.
    */
    const parRaison = new Map<string, string[]>();
    for (const e of serie.ecartes) {
      const liste = parRaison.get(e.raison) ?? [];
      liste.push(jourCourt(e.date, e.jourRelatif));
      parRaison.set(e.raison, liste);
    }
    for (const [raison, jours] of parRaison) {
      morceaux.push(
        jours.length > 6
          ? `${String(jours.length)} journées non affectées : ${raison}.`
          : `Pas le ${jours.join(", ")} : ${raison}.`,
      );
    }
  }

  // Dit même quand rien n'a été posé : c'est précisément quand on ne voit rien qu'il faut savoir
  // qu'on n'a pas pu regarder.
  if (serie.absencesNonVerifiees && (serie.poses.length > 0 || serie.ecartes.length > 0)) {
    morceaux.push("Les absences n'ont pas pu être vérifiées : à contrôler.");
  }

  return morceaux.length === 0 ? null : morceaux.join(" ");
}
